/**
 * OperationDataModal.js
 *
 * Modal to capture operation-specific data before adding checks to the log.
 * Shows different fields based on operation type.
 *
 * Flow:
 * 1. User selects operation type from FloatingActionBar
 * 2. This modal appears with fields for that operation type
 * 3. User fills fields and submits
 * 4. onSubmit is called with (operationType, data, timeOptions)
 * 5. Parent adds section + operation_data + checks to unifiedLog
 *
 * FT-284: now hosts <InlineTimeField> above the action row. Submit is
 * Rules-of-Hooks safe — useInlineEventTime is called UNCONDITIONALLY at the
 * top, and the empty-fields auto-submit side-effect is gated by a submittedRef
 * so StrictMode's dev-time double-invoke does NOT fire twice. handleSubmit
 * gates on `timeField.isValid` BEFORE `validate()` to prevent Enter-key bypass.
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, AlertCircle } from 'lucide-react';
import {
  OPERATION_CHECKLISTS,
  OPERATION_TYPE_LABELS,
  getOperationTemplate
} from '../../../../../config/operationsConfig';
import { useStorageState } from '../../../../../hooks/useStorageState';
import { useInlineEventTime } from './useInlineEventTime';
import InlineTimeField from './InlineTimeField';
import ModalErrorBanner from './ModalErrorBanner';

const OperationDataModal = ({
  operationType,
  onSubmit,
  onCancel,
  vesselTimezone
}) => {
  // ---------- ALL HOOKS UNCONDITIONAL, TOP-LEVEL (FT-284 §10.5) ----------
  const timeField = useInlineEventTime(vesselTimezone);

  // Load voyage settings to get custom operation templates.
  // IMPORTANT: Must use 'voyage_settings' to match VoyageSettingsModal save key.
  const { value: voyageSettings } = useStorageState('voyage_settings', null);

  // Get field definitions from custom templates (falls back to defaults).
  const template = getOperationTemplate(operationType, voyageSettings);
  const fields = template?.fields || [];
  const operationLabel = template?.label || OPERATION_TYPE_LABELS?.[operationType] || operationType;

  // Initialize form state
  const [formData, setFormData] = useState(() => {
    const initial = {};
    fields.forEach(field => {
      initial[field.id] = '';
    });
    return initial;
  });

  const [errors, setErrors] = useState({});
  const [boundaryError, setBoundaryError] = useState(null);
  const submittedRef = useRef(false);
  const submitInProgressRef = useRef(false);

  // Empty-fields auto-submit: side effect gated by submittedRef, hook always called.
  // FT-284 §10.5 — Rules-of-Hooks safe: useEffect is unconditional; the BODY
  // is the side-effect gate.
  useEffect(() => {
    if (fields.length === 0 && !submittedRef.current && operationType) {
      submittedRef.current = true;
      // timeField initialised → "now" snapshot is fresh.
      onSubmit(operationType, {}, timeField.freshSnapshot());
    }
  }, [fields.length, operationType, onSubmit, timeField]);

  // Early return AFTER all hooks — safe.
  if (!operationType || fields.length === 0) {
    return null;
  }

  // Handle field change
  const handleChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
    // Clear error on change
    if (errors[fieldId]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  };

  // Validate form
  const validate = () => {
    const newErrors = {};
    fields.forEach(field => {
      if (field.required && (!formData[field.id] || formData[field.id] === '')) {
        newErrors[field.id] = `${field.label} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    // FT-284: gate BEFORE validate so Enter-key cannot bypass time validity.
    if (!timeField.isValid || submitInProgressRef.current) return;
    if (!validate()) return;
    submitInProgressRef.current = true;
    setBoundaryError(null);
    try {
      // Convert number fields
      const processedData = { ...formData };
      fields.forEach(field => {
        if (field.type === 'number' && processedData[field.id]) {
          processedData[field.id] = parseInt(processedData[field.id], 10);
        }
      });
      const timeOptions = timeField.freshSnapshot();
      const result = await onSubmit(operationType, processedData, timeOptions);
      if (result?.boundaryError) {
        setBoundaryError(result.boundaryError);
        return;  // modal stays open; no onCancel
      }
      // Parent (VoyageDocument) owns close on success via setShowOperationModal(null).
    } finally {
      submitInProgressRef.current = false;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onCancel}
      />

      {/* Modal */}
      {/* FT-284: scroll containment — outer caps at 90vh, header is fixed,
          form scrolls if its contents (esp. structured-reason dropdown) exceed
          the viewport on small phones. */}
      <div className="relative bg-white w-full sm:w-96 sm:rounded-lg shadow-xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-semibold text-slate-800">{operationLabel}</h3>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-slate-200 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* Form (scrollable region) */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {fields.map(field => (
              <div key={field.id}>
                {/* Label */}
                <label
                  htmlFor={field.id}
                  className="block text-sm font-medium text-slate-700 mb-1"
                >
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>

                {/* Input based on field type */}
                {field.type === 'select' ? (
                  <div className="space-y-2">
                    {/* Button Grid for Select Options - mobile-friendly */}
                    <div className="grid grid-cols-2 gap-2">
                      {(field.options || []).map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => handleChange(field.id, opt.value)}
                          className={`
                            px-4 py-3 min-h-[56px] rounded-lg text-sm font-medium transition-colors
                            border-2
                            ${formData[field.id] === opt.value
                              ? 'bg-cyan-50 border-cyan-500 text-cyan-700 ring-2 ring-cyan-300'
                              : errors[field.id]
                                ? 'bg-white border-red-300 text-slate-700 hover:border-red-400'
                                : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                            }
                          `}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {!formData[field.id] && (
                      <p className="text-xs text-slate-500">Tap to select {field.label.toLowerCase()}</p>
                    )}
                  </div>
                ) : field.type === 'radio' ? (
                  <div className="grid grid-cols-2 gap-2">
                    {(field.options || []).map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleChange(field.id, opt.value)}
                        className={`
                          px-4 py-3 min-h-[56px] rounded-lg text-sm font-medium transition-colors
                          border-2
                          ${formData[field.id] === opt.value
                            ? 'bg-cyan-50 border-cyan-500 text-cyan-700 ring-2 ring-cyan-300'
                            : errors[field.id]
                              ? 'bg-white border-red-300 text-slate-700 hover:border-red-400'
                              : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                          }
                        `}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                ) : field.type === 'number' ? (
                  <input
                    type="number"
                    id={field.id}
                    value={formData[field.id]}
                    onChange={(e) => handleChange(field.id, e.target.value)}
                    placeholder={field.placeholder || ''}
                    min="0"
                    className={`
                      w-full px-3 py-3 min-h-[56px] text-base border rounded-lg transition-colors
                      focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500
                      ${errors[field.id] ? 'border-red-300 bg-red-50' : 'border-slate-300'}
                    `}
                  />
                ) : (
                  <input
                    type="text"
                    id={field.id}
                    value={formData[field.id]}
                    onChange={(e) => handleChange(field.id, e.target.value)}
                    placeholder={field.placeholder || ''}
                    className={`
                      w-full px-3 py-3 min-h-[56px] text-base border rounded-lg transition-colors
                      focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500
                      ${errors[field.id] ? 'border-red-300 bg-red-50' : 'border-slate-300'}
                    `}
                  />
                )}

                {/* Error message */}
                {errors[field.id] && (
                  <div className="flex items-center gap-1 mt-1 text-sm text-red-600">
                    <AlertCircle className="h-3 w-3" />
                    <span>{errors[field.id]}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* FT-283 §G: inline time field + boundary banner just above the
              in-form action buttons. In-form variant — no bg-white wrapper or
              border, just a top margin + spacing. */}
          <div className="mt-6 mb-4 space-y-3">
            {boundaryError && (
              <ModalErrorBanner message={boundaryError.humanMessage} />
            )}
            <InlineTimeField
              timeField={timeField}
              vesselTimezone={vesselTimezone}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-3 min-h-[56px] border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-base font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!timeField.isValid}
              className="flex-1 px-4 py-3 min-h-[56px] bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-base font-medium"
            >
              Start Operation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OperationDataModal;
